import { useLazyQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from "react";
import {blue} from "@mui/material/colors";

import {
  LOOKUP_SESSION_WITH_CHILDREN,
  LOOKUP_SESSION_WITH_REGION_COUNT,
} from "../../queries/GraphQLQueries";
import ChildBirthdayIndicatorIcon from "../common/ChildBirthdayIndicatorIcon";
import ChildDetailsChip from "../common/ChildDetailsChip";
import ChildChipWithTagBadges from "./ChildChipWithTagBadges";
import HistoricalSessionTooltip from "../common/HistoricalSessionTooltip";
import MemberRegionChip from "./MemberRegionChip";


const styles = {
    box: {
        childInfoBoxStyle: {
            display: "flex",
            justifyContent: 'flex-start',
            flex: '1 1 0',
            minWidth: 0,
            maxWidth: 'fit-content'
        },
        childInfoContainer: {
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '0.5rem',
            marginTop: '0.25rem'
        },
        history: {
            display: "flex",
            justifyContent: 'flex-start',
            marginTop: "0.3rem"
        }
    },
    typography: {
        title: (showHistory) => ({
            color: showHistory ? blue[600] : "black",
            display: "flex",
            fontWeight: 600,
            justifyContent: 'flex-start',
            overflow: "hidden",
            paddingRight: "0.6rem",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
        })
    }
};

export default function SessionMemberDetails({selectedSessionEvent, serviceType, showHistory = true}) {
    const BRIEF_VIEW_THRESHOLD = 3;

    const titleStyle = styles.typography.title(showHistory);

    const [loadingData, setLoadingData] = useState(true);
    const [showBriefView, setShowBriefView] = useState(false);
    const [bookings, setBookings] = useState([]);

    const [sessionInfoWithChildren, { data }] = useLazyQuery(LOOKUP_SESSION_WITH_CHILDREN);
    const [sessionInfoWithRegionCount, { data: sessionInfoWithRegionCountData }] = useLazyQuery(LOOKUP_SESSION_WITH_REGION_COUNT);

    useEffect(() => {

        if (!selectedSessionEvent) {
            return;
        }

        const briefView = selectedSessionEvent.bookingsCount > BRIEF_VIEW_THRESHOLD;
        setShowBriefView(briefView);

        if (briefView) {
            sessionInfoWithRegionCount({variables: {sessionId: selectedSessionEvent.id}});
        } else {
            sessionInfoWithChildren({variables: {sessionId: selectedSessionEvent.id}});
        }
    }, [selectedSessionEvent]);

    useEffect(() => {
        setLoadingData(false);

        if (data && data?.sessionInfo) {
            const childrenInSession = data?.sessionInfo.seats?.filter(seat => !!seat.booking).map(seat => seat.booking);
            setBookings(childrenInSession);
        }
    }, [data, sessionInfoWithRegionCountData]);
    
    return (
      <>
        {!loadingData && showBriefView && (
          <Grid marginTop={3}>
            <MemberRegionChip regionDetails={sessionInfoWithRegionCountData} />
          </Grid>
        )}
        {!loadingData && !showBriefView && showHistory && (
          <Grid container rowSpacing={1} columnSpacing={2} justifyContent='space-between'>
          <Grid item size={6}>
              <Typography sx={titleStyle} variant="body2">Child Information</Typography>
          </Grid>
          <Grid item size={6}>
              <Typography sx={titleStyle} variant="body2">History</Typography>
          </Grid>

          {bookings?.map(booking => {
              const {child, attendedSessionReport: attendanceReport} = booking;

              return <React.Fragment key={child.id}>
                  <Grid item size={6}>
                    <Box sx={styles.box.childInfoBoxStyle}>
                        <ChildDetailsChip
                            child={child}
                            compactView={false}
                            showHistory={showHistory}
                            serviceType={serviceType}
                            attendanceReport={attendanceReport}
                        />
                        <ChildBirthdayIndicatorIcon dateOfBirth={child?.dateOfBirth} />
                    </Box>
                  </Grid>

                  <Grid item size={6}>
                    <Box sx={styles.box.history}>
                      <HistoricalSessionTooltip {...{ serviceType, attendanceReport }} />
                    </Box>
                  </Grid>
                </React.Fragment>
          })}
          </Grid>
        )}
        {!loadingData && !showBriefView && !showHistory && (
          <Box>
            <Typography sx={titleStyle} variant="body2">Child Information</Typography>
            <Box sx={styles.box.childInfoContainer}>
              {bookings?.map(booking => {
                  const {child, attendedSessionReport: attendanceReport, id} = booking;

                  return (
                    <Box key={child.id} sx={styles.box.childInfoBoxStyle}>
                        <ChildChipWithTagBadges
                            child={child}
                            compactView={false}
                            showHistory={showHistory}
                            serviceType={serviceType}
                            attendanceReport={attendanceReport}
                            bookingId={id}
                            sessionStartTime={selectedSessionEvent?.from}
                        />
                        <ChildBirthdayIndicatorIcon dateOfBirth={child?.dateOfBirth} />
                    </Box>
                  );
              })}
            </Box>
          </Box>
        )}
      </>
    );
}
